import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, mediaSrc } from "../lib/api";
import { Sparkles, Loader2, Wand2, Eye, EyeOff, ImagePlus, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const IDEAS = [
  "A moody cinematic portrait of a man in a black leather harness, neon blue rim light",
  "Minimalist neon vault logo icon, electric blue on black, no text",
  "A rugged biker in denim and boots at a garage, film grain, dramatic shadows",
];

export default function AiCreate() {
  const nav = useNavigate();
  const [prompt, setPrompt] = useState("");
  const [makePublic, setMakePublic] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    const p = prompt.trim();
    if (!p) { toast.error("Describe the image you want"); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/ai/generate-image", { prompt: p, private: !makePublic });
      setResult(data.media);
      toast.success(makePublic ? "Image created & published!" : "Image created — saved as a private draft.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Generation failed — try a different prompt.");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-[1000px] mx-auto px-4 sm:px-6 pt-20 pb-16" data-testid="ai-create-page">
      <div className="flex items-center gap-3 mb-2">
        <ImagePlus className="text-[#A855F7]" size={30} />
        <h1 className="font-heading text-4xl sm:text-5xl uppercase tracking-tighter text-white">Create with <span className="text-[#A855F7]">AI</span></h1>
      </div>
      <p className="text-[#A1A1AA] text-sm mb-6 max-w-2xl">Generate images from a text prompt.</p>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <textarea
            data-testid="ai-create-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={5}
            placeholder="Describe the image… e.g. a man in a black leather harness under neon blue light, cinematic"
            className="w-full bg-[#141414] border border-[#1C1C1F] text-white px-4 py-3 focus:outline-none focus:border-[#A855F7] transition-colors placeholder:text-[#52525B] rounded-xl text-sm resize-none"
          />
          <div className="flex flex-wrap gap-2 mt-3" data-testid="ai-create-ideas">
            {IDEAS.map((i) => (
              <button key={i} onClick={() => setPrompt(i)} className="text-left text-[#A1A1AA] hover:text-white border border-[#1C1C1F] hover:border-[#A855F7] rounded-lg px-3 py-1.5 text-[11px] transition-colors max-w-full truncate">{i}</button>
            ))}
          </div>

          <div className="flex items-center justify-between gap-3 mt-4">
            <button
              type="button"
              data-testid="ai-create-visibility"
              onClick={() => setMakePublic((v) => !v)}
              className={`flex items-center gap-2 font-heading uppercase tracking-widest text-[11px] border px-3 py-2 rounded-lg transition-colors ${makePublic ? "border-[#31A24C] text-[#31A24C]" : "border-[#1C1C1F] text-[#A1A1AA] hover:text-white"}`}
            >
              {makePublic ? <Eye size={13} /> : <EyeOff size={13} />} {makePublic ? "Public" : "Private draft"}
            </button>
            <button data-testid="ai-create-generate" onClick={generate} disabled={loading || !prompt.trim()} className="flex-1 bg-[#A855F7] text-white font-heading uppercase tracking-widest text-xs px-5 py-3 hover:bg-[#9333EA] transition-colors disabled:opacity-50 flex items-center justify-center gap-2 rounded-xl">
              {loading ? <Loader2 className="animate-spin" size={16} /> : <Wand2 size={16} />} {loading ? "Generating…" : "Generate"}
            </button>
          </div>
        </div>

        <div className="bg-[#0A0A0A] border border-[#1C1C1F] rounded-xl min-h-[320px] flex items-center justify-center overflow-hidden" data-testid="ai-create-result">
          {loading ? (
            <div className="flex flex-col items-center text-[#52525B]">
              <Loader2 className="animate-spin text-[#A855F7] mb-3" size={34} />
              <p className="font-mono text-xs uppercase tracking-widest">Painting pixels…</p>
            </div>
          ) : result ? (
            <div className="w-full">
              <img data-testid="ai-create-image" src={mediaSrc(result.url)} alt={result.title} className="w-full object-cover block" />
              <button onClick={() => nav(`/media/${result.id}`)} data-testid="ai-create-view" className="w-full bg-[#141414] hover:bg-[#1C1C1F] text-white font-heading uppercase tracking-widest text-[11px] py-3 flex items-center justify-center gap-2 transition-colors">
                View post <ArrowRight size={14} />
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center text-[#2A2A2E]">
              <Sparkles size={40} className="mb-3" />
              <p className="font-mono text-xs uppercase tracking-widest text-[#52525B]">Your image appears here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
